import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {Button} from '../../../common/components';
import ErrorList from '../ErrorList';
import ScrollToMe from '../../../common/components/ScrollToMe';
import {
  getPropByPath,
  setPropByPath,
} from '../../../common/utils/property-path';
import styles from './Form.css';

// TODO: write test; how does this handle array values, objects, etc?
export const checkIsDirty = (oldState, newState) => {
  return Object.keys(newState).some(key => {
    if (typeof newState[key] === 'object' && oldState[key]) {
      return checkIsDirty(oldState[key], newState[key]);
    }
    return newState[key] !== oldState[key];
  });
};

export const formatZipCode = zipCode => {
  const zip = scrubNumber(zipCode);
  if (zip.length > 5) {
    return zip.slice(0, 5);
  }
  return zip;
};

export const scrubNumber = number => {
  return (number || '').toString().replace(/\D/g, '');
};

export const formatPhoneNumber = (format, phone) => {
  const scrubbedPhone = scrubNumber(phone);
  const matchArr = scrubbedPhone.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  const str = format.split('').reduce((acc, curr) => {
    if (curr === '$') {
      // throw out $
      return acc;
    }
    const number = parseInt(curr, 10);
    if (!isNaN(number)) {
      const match = matchArr[number];
      if (!match.length) {
        return acc.slice(0, acc.length - 1) + match;
      }
      return acc + match;
    }
    return acc + curr;
  }, '');
  return str;
};

export const validateFormInput = (errors, input) => {
  const { name, label, pattern, required, value } = input;
  if (required && !value.trim().length) {
    return setPropByPath(errors, name, `${label} is required.`);
  }
  if (pattern && !new RegExp(pattern).exec(value.trim())) {
    return setPropByPath(
      errors,
      name,
      input['data-validationMsg'] || 'Field value does not match pattern.'
    );
  }
  return {};
};

export const buildInputAttribFactory = state => ({
  type,
  name,
  label,
  value,
  checked,
  error,
  disabled,
  ...rest
}) => {
  let valuePath = name || '';
  if (typeof name === 'function') {
    valuePath = name(state.form);
  }
  const nameParts = valuePath.split('.');
  const defaultName = nameParts[nameParts.length - 1];
  const defaultLabel = defaultName.length
    ? defaultName[0].toUpperCase() + defaultName.slice(1)
    : '';

  // if field is a checkbox, it will not have a type, value, or error attribute and will instead need a checked attribute
  const fieldValue =
    type === 'checkbox'
      ? {
          checked: checked || getPropByPath(state.form, valuePath) || false,
        }
      : {
          value: value || getPropByPath(state.form, valuePath) || '',
          error:
            error || state.errors
              ? getPropByPath(state.errors, valuePath)
              : null,
          type,
        };
  return {
    name: valuePath,
    label: label || defaultLabel,
    disabled:
      typeof disabled === 'string'
        ? getPropByPath(state.form, disabled) || false
        : disabled,
    ...fieldValue,
    ...rest,
  };
};

export default class Form extends React.PureComponent {
  static propTypes = {
    initState: PropTypes.object,
    formInputs: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.object, PropTypes.func])
    ).isRequired,
    formErrors: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.shape({
        success: PropTypes.bool,
        errors: PropTypes.arrayOf(PropTypes.string),
      }),
    ]),
    formSubmit: PropTypes.func.isRequired,
    children: PropTypes.node,
    className: PropTypes.string,
    renderSubmit: PropTypes.func,
    formDisabled: PropTypes.bool,
    warnOnExit: PropTypes.bool,
    preventSubmit: PropTypes.bool,
    listenToState: PropTypes.bool,
    FormWrapper: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      isDirty: false,
      errors: {},
      form: props.initState,
    };
  }

  componentWillReceiveProps({ initState }) {
    // if we want to listen to state changes, update when props change
    if (this.props.listenToState) {
      this.setState(prevState => ({
        ...prevState,
        form: {
          ...prevState.form,
          ...initState,
        },
      }));
    }
  }

  componentDidMount() {
    if (this.props.warnOnExit) {
      window.addEventListener('beforeunload', this.onUnload);
    }
  }

  componentWillUnmount() {
    if (this.props.warnOnExit) {
      window.removeEventListener('beforeunload', this.onUnload);
    }
  }

  onUnload = event => {
    if (this.state.isDirty) {
      event.returnValue = this.state.isDirty;
      return this.state.isDirty;
    }
  };

  hasValidationErrors = errors => {
    if (!errors) {
      return false;
    }
    return Object.keys(errors).some(key => {
      const err = errors[key];
      if (typeof err === 'object') {
        return this.hasValidationErrors(err);
      }
      return err && err.length;
    });
  };

  handleSubmit = evt => {
    evt.preventDefault();

    if (this.props.preventSubmit && !this.state.isDirty) {
      return false;
    }

    // validate any untouched fields
    const uncheckedErrors = this.props.formInputs
      .filter(input => typeof input !== 'function')
      .reduce((acc, curr) => {
        const input = this.getInputAttributes(curr);
        return {
          ...acc,
          ...validateFormInput(acc, input),
        };
      }, {});
    const hasUncheckedErrors = this.hasValidationErrors(uncheckedErrors);

    // check for any validation errors
    const hasValidationErrors = this.hasValidationErrors(this.state.errors);

    if (hasValidationErrors || hasUncheckedErrors) {
      return hasUncheckedErrors
        ? this.setState(prevState => {
            return {
              ...prevState,
              errors: {
                ...prevState.errors,
                ...uncheckedErrors,
              },
            };
          })
        : undefined;
    }

    // expose form values, relevant update functions, and event to parent
    return this.props.formSubmit(
      {
        form: this.state.form,
        isDirty: this.state.isDirty,
        handleErrors: this.handleDetailedErrors,
        updateField: this.updateFormField,
        event: evt,
      },
      (err, data) => {
        if (!err) {
          // handle any data from success
          const newState = data ? { initState: data } : {};
          this.setState(prevState => ({
            ...prevState,
            isDirty: false,
            ...newState,
          }));
        } else {
          // handle errors
          this.handleDetailedErrors(data);
        }
      }
    );
  };

  // expects array of { propertyName, message }
  handleDetailedErrors = detailedErrors => {
    return this.setState(prevState => {
      const errors = detailedErrors.reduce((acc, curr) => {
        return setPropByPath(acc, curr.propertyName, curr.message);
      }, prevState.errors);
      const newState = {
        ...prevState,
        errors,
      };
      return newState;
    });
  };

  updateFormField = ({ fieldName, value, error }) => {
    return this.setState(prevState => {
      const updatedForm = setPropByPath(prevState.form, fieldName, value);
      const updatedErrors = setPropByPath(prevState.errors, fieldName, error);
      const newState = {
        ...prevState,
        form: updatedForm,
        errors: {
          ...updatedErrors,
          form: [],
        },
      };
      return {
        ...newState,
        isDirty: checkIsDirty(this.props.initState, newState.form),
      };
    });
  };

  handleChange = (input, checkOnChange) => value => {
    const { name } = input;
    let newValue = value;
    let error;
    // checkOnChange should accept value
    // if no update should be performed, return false
    // otherwise, return update value
    if (checkOnChange && typeof checkOnChange === 'function') {
      newValue = checkOnChange(value, this.state.form);
    }

    // checkOnChange can return an object containing an error as well as a value
    if (typeof newValue === 'object' && typeof newValue.value === 'string') {
      error = newValue.error;
      newValue = newValue.value;
    }

    // check pattern validation
    if (input.pattern || input.required) {
      error =
        error ||
        getPropByPath(
          validateFormInput(this.state.errors, { ...input, value: newValue }),
          name
        );
    }

    return this.updateFormField({
      fieldName: name,
      value: newValue,
      error,
    });
  };

  handleOnBlur = input => evt => {
    evt.preventDefault();

    this.setState(prevState => ({
      ...prevState,
      errors: validateFormInput(prevState.errors, input),
    }));
  };

  getInputAttributes = input => {
    const inputAttribFactory = buildInputAttribFactory(this.state);
    const { onChange, ...rest } = input;

    const attribs = inputAttribFactory(rest);
    const inputProps = {
      ...attribs,
      onChange: this.handleChange(attribs, onChange),
    };

     if (input.pattern || input.required) {
       return {
         ...inputProps,
         onBlur: this.handleOnBlur(inputProps),
       };
     }

    return inputProps;
  };

  renderFormContents = () => {
    const { children, formErrors, formInputs = [] } = this.props;

    let errorListProps = formErrors;
    if (Array.isArray(formErrors)) {
      errorListProps = {
        errors: formErrors,
      };
    }

    return (
      <div>
        {children}
        {errorListProps.errors.length ? (
          <div>
            <ScrollToMe />
            <ErrorList {...errorListProps} />
          </div>
        ) : null}
        {formInputs.map((input, index) => {
          if (typeof input === 'function') {
            return input();
          }
          const { Component, ...rest } = input;
          const inputProps = this.getInputAttributes({ ...rest });
          if (Component) {
            return <Component {...inputProps} key={index} />;
          }
          return null;
        })}
      </div>
    );
  };

  render() {
    const {
      formDisabled = false,
      className,
      renderSubmit,
      preventSubmit,
      FormWrapper,
    } = this.props;

    return (
      <form
        onSubmit={this.handleSubmit}
        className={classnames(styles.form, className)}
      >
        {FormWrapper ? (
          <FormWrapper>{this.renderFormContents()}</FormWrapper>
        ) : (
          this.renderFormContents()
        )}
        {renderSubmit && typeof renderSubmit === 'function' ? (
          renderSubmit(this.state.isDirty)
        ) : (
          <div className={styles.buttonContainer}>
            <Button
              type="submit"
              label="submit"
              disabled={
                preventSubmit && !this.state.isDirty ? true : formDisabled
              }
              className={styles.submitButton}
            />
          </div>
        )}
      </form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/Form/index.js